import matter from "gray-matter";
import { z } from "@brains/utils";
import type { BaseEntity, ContentVisibility } from "./types";
import { contentVisibilitySchema } from "./types";

/**
 * Configuration for frontmatter handling
 */
export interface FrontmatterConfig<T extends BaseEntity> {
  /**
   * Fields to explicitly include in frontmatter
   * If not specified, includes all non-system fields
   */
  includeFields?: (keyof T)[];

  /**
   * Fields to exclude from frontmatter
   * By default excludes: id, entityType, content, created, updated
   */
  excludeFields?: (keyof T)[];

  /**
   * Custom serializers for complex fields
   */
  customSerializers?: {
    [K in keyof T]?: (value: T[K]) => unknown;
  };

  /**
   * Custom deserializers for complex fields
   */
  customDeserializers?: {
    [K in keyof T]?: (value: unknown) => T[K];
  };
}

// Default system fields that should not be in frontmatter
const DEFAULT_SYSTEM_FIELDS: Array<keyof BaseEntity> = [
  "id",
  "entityType",
  "content",
  "contentHash",
  "created",
  "updated",
  "visibility",
];

/**
 * Extract metadata fields from an entity for frontmatter
 * Returns only non-system fields by default
 */
export function extractMetadata<T extends BaseEntity>(
  entity: T,
  config?: FrontmatterConfig<T>,
): Record<string, unknown> {
  const { includeFields, excludeFields = [], customSerializers } = config ?? {};
  const excludedFieldNames = new Set<string>([
    ...DEFAULT_SYSTEM_FIELDS.map(String),
    ...excludeFields.map(String),
  ]);

  const metadata: Record<string, unknown> = {};

  // Get all fields from the entity
  const allFields = Object.keys(entity) as Array<keyof T>;

  // Determine which fields to include
  let fieldsToProcess: Array<keyof T>;
  if (includeFields) {
    // If includeFields is specified, only include those
    fieldsToProcess = includeFields.filter(
      (field) => !excludedFieldNames.has(String(field)),
    );
  } else {
    // Otherwise include all fields except excluded ones
    fieldsToProcess = allFields.filter(
      (field) => !excludedFieldNames.has(String(field)),
    );
  }

  // Process each field
  for (const field of fieldsToProcess) {
    const value = entity[field];

    // Skip undefined values
    if (value === undefined) {
      continue;
    }

    // Use custom serializer if available
    if (customSerializers && field in customSerializers) {
      const serializer = customSerializers[field];
      if (serializer) {
        metadata[field as string] = serializer(value);
      }
    } else {
      metadata[field as string] = value;
    }
  }

  return metadata;
}

/**
 * Generate markdown with frontmatter from content and metadata
 */
export function generateMarkdownWithFrontmatter(
  content: string,
  metadata: Record<string, unknown>,
): string {
  // Only add frontmatter if there's metadata
  if (Object.keys(metadata).length === 0) {
    return content;
  }

  const cleaned = Object.fromEntries(
    Object.entries(metadata).filter(([, v]) => v !== undefined),
  );
  return matter.stringify(content, cleaned);
}

/**
 * Helper to convert all Date objects to ISO strings recursively
 */
function convertDatesToStrings(obj: unknown): unknown {
  if (obj instanceof Date) {
    return obj.toISOString();
  }
  if (Array.isArray(obj)) {
    return obj.map(convertDatesToStrings);
  }
  if (obj !== null && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertDatesToStrings(value);
    }
    return result;
  }
  return obj;
}

/**
 * Parse markdown with frontmatter into content and metadata
 */
export function parseMarkdownWithFrontmatter<T>(
  markdown: string,
  schema: z.ZodSchema<T>,
): {
  content: string;
  metadata: T;
} {
  const { content, data } = matter(markdown);

  // Convert all Date objects to strings before parsing with Zod
  const normalizedData = convertDatesToStrings(data);

  return {
    content: content.trim(),
    metadata: schema.parse(normalizedData),
  };
}

/**
 * Apply custom deserializers to metadata
 */
export function deserializeMetadata<T extends BaseEntity>(
  metadata: Record<string, unknown>,
  config?: FrontmatterConfig<T>,
): Record<string, unknown> {
  if (!config?.customDeserializers) {
    return metadata;
  }

  const result: Record<string, unknown> = { ...metadata };

  for (const [field, deserializer] of Object.entries(
    config.customDeserializers,
  )) {
    if (field in metadata) {
      result[field] = deserializer(metadata[field]);
    }
  }

  return result;
}

/**
 * Generate frontmatter string from metadata
 */
export function generateFrontmatter(metadata: Record<string, unknown>): string {
  if (Object.keys(metadata).length === 0) {
    return "";
  }

  // Use gray-matter to generate frontmatter
  const fullMarkdown = matter.stringify("", metadata);

  // Extract just the frontmatter part
  const match = fullMarkdown.match(/^---\n[\s\S]*?\n---/);
  return match ? match[0] : "";
}

const visibilityFrontmatterSchema = z.object({
  visibility: contentVisibilitySchema,
});

export function extractVisibilityFromMarkdown(
  markdown: string,
): ContentVisibility {
  const parsed = matter(markdown);
  return visibilityFrontmatterSchema.parse(parsed.data).visibility;
}

export function hasVisibilityFrontmatter(markdown: string): boolean {
  const frontmatterMatch = markdown.match(/^---\r?\n[\s\S]*?\r?\n---/);
  const visibilityMatch = frontmatterMatch?.[0].match(/^visibility:/m);
  return visibilityMatch !== null && visibilityMatch !== undefined;
}

export function applyVisibilityToMarkdown(
  markdown: string,
  visibility: ContentVisibility,
): string {
  if (visibility === "public" && !hasVisibilityFrontmatter(markdown)) {
    return markdown;
  }

  const parsed = matter(markdown);
  const frontmatter = Object.fromEntries(
    Object.entries(parsed.data).filter(([key]) => key !== "visibility"),
  );

  if (visibility !== "public") {
    frontmatter["visibility"] = visibility;
  }

  return generateMarkdownWithFrontmatter(parsed.content.trim(), frontmatter);
}
